import * as SecureStore from 'expo-secure-store';
import * as React from 'react';
import { AppState, Platform } from 'react-native';
import { create } from 'zustand';

export type AppearancePreference = 'auto' | 'day' | 'night';
export type ResolvedAppearanceMode = 'day' | 'night';

const APPEARANCE_KEY = 'appearance_preference';
const DAY_START_HOUR = 7;
const NIGHT_START_HOUR = 19;

async function readAppearancePreference(): Promise<AppearancePreference> {
  try {
    if (Platform.OS === 'web') {
      const value = localStorage.getItem(APPEARANCE_KEY);
      return isAppearancePreference(value) ? value : 'auto';
    }

    const value = await SecureStore.getItemAsync(APPEARANCE_KEY);
    return isAppearancePreference(value) ? value : 'auto';
  } catch {
    return 'auto';
  }
}

async function writeAppearancePreference(preference: AppearancePreference) {
  try {
    if (Platform.OS === 'web') {
      localStorage.setItem(APPEARANCE_KEY, preference);
      return;
    }

    await SecureStore.setItemAsync(APPEARANCE_KEY, preference);
  } catch {
    // best-effort
  }
}

function isAppearancePreference(value: string | null | undefined): value is AppearancePreference {
  return value === 'auto' || value === 'day' || value === 'night';
}

export function resolveAppearanceMode(
  preference: AppearancePreference,
  now: Date = new Date()
): ResolvedAppearanceMode {
  if (preference === 'day' || preference === 'night') {
    return preference;
  }

  const hour = now.getHours();
  return hour >= DAY_START_HOUR && hour < NIGHT_START_HOUR ? 'day' : 'night';
}

export function getNextAppearanceBoundary(now: Date = new Date()) {
  const next = new Date(now);
  const hour = now.getHours();

  if (hour < DAY_START_HOUR) {
    next.setHours(DAY_START_HOUR, 0, 0, 0);
    return next;
  }

  if (hour < NIGHT_START_HOUR) {
    next.setHours(NIGHT_START_HOUR, 0, 0, 0);
    return next;
  }

  next.setDate(next.getDate() + 1);
  next.setHours(DAY_START_HOUR, 0, 0, 0);
  return next;
}

type AppearanceStore = {
  isHydrated: boolean;
  preference: AppearancePreference;
  resolvedMode: ResolvedAppearanceMode;
  hydrate: () => Promise<void>;
  setPreference: (preference: AppearancePreference) => Promise<void>;
  setRouteOverride: (mode: ResolvedAppearanceMode | null) => void;
  refreshResolvedMode: () => void;
  routeOverrideMode: ResolvedAppearanceMode | null;
};

export const useAppearance = create<AppearanceStore>((set, get) => ({
  isHydrated: false,
  preference: 'auto',
  resolvedMode: resolveAppearanceMode('auto'),
  routeOverrideMode: null,

  async hydrate() {
    const preference = await readAppearancePreference();
    const routeOverrideMode = get().routeOverrideMode;
    set({
      isHydrated: true,
      preference,
      resolvedMode: routeOverrideMode ?? resolveAppearanceMode(preference),
    });
  },

  async setPreference(preference) {
    await writeAppearancePreference(preference);
    const routeOverrideMode = get().routeOverrideMode;
    set({
      preference,
      resolvedMode: routeOverrideMode ?? resolveAppearanceMode(preference),
    });
  },

  setRouteOverride(mode) {
    const preference = get().preference;
    set({
      routeOverrideMode: mode,
      resolvedMode: mode ?? resolveAppearanceMode(preference),
    });
  },

  refreshResolvedMode() {
    const preference = get().preference;
    const routeOverrideMode = get().routeOverrideMode;
    set({ resolvedMode: routeOverrideMode ?? resolveAppearanceMode(preference) });
  },
}));

export function useAppearanceScheduler(isEnabled: boolean) {
  const refreshResolvedMode = useAppearance((state) => state.refreshResolvedMode);
  const preference = useAppearance((state) => state.preference);
  const resolvedMode = useAppearance((state) => state.resolvedMode);

  React.useEffect(() => {
    if (!isEnabled) {
      return;
    }

    refreshResolvedMode();

    const appStateSubscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') {
        refreshResolvedMode();
      }
    });

    if (preference !== 'auto') {
      return () => {
        appStateSubscription.remove();
      };
    }

    const boundary = getNextAppearanceBoundary();
    const delay = Math.max(boundary.getTime() - Date.now(), 1000);
    const timeoutId = setTimeout(() => {
      refreshResolvedMode();
    }, delay);

    return () => {
      appStateSubscription.remove();
      clearTimeout(timeoutId);
    };
  }, [isEnabled, preference, refreshResolvedMode, resolvedMode]);
}

export function getAppearanceColorScheme(mode: ResolvedAppearanceMode) {
  return mode === 'day' ? 'light' : 'dark';
}
